import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertTriangle,
  DollarSign,
  User,
  Calendar,
  FileText,
  Users,
  CheckCircle2,
  ExternalLink,
  Paperclip,
  Loader2,
  Hash,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

const fmtCur = (n) =>
  Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-AR'); } catch { return iso; }
};

function Header({ p, variant }) {
  const color = variant === 'actual' ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200';
  const badge = variant === 'actual' ? 'bg-blue-600' : 'bg-orange-600';
  return (
    <div className={`rounded-lg border ${color} p-3`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] uppercase tracking-wider text-white px-2 py-0.5 rounded ${badge} font-bold`}>
          {variant === 'actual' ? 'Caso actual' : 'Caso anterior'}
        </span>
        <span className="text-[11px] text-slate-500">{p?.estado || '—'}</span>
      </div>
      <div className="font-semibold text-slate-800 text-sm truncate" title={p?.titulo}>
        {p?.titulo || p?.id?.slice(0, 8)}
      </div>
      <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-600">
        <span className="flex items-center gap-1"><Calendar size={11} />{fmtDate(p?.created_at)}</span>
        <span className="flex items-center gap-1"><User size={11} />{p?.responsable_nombre || '—'}</span>
        <span className="flex items-center gap-1">Score: <strong className={p?.score_riesgo >= 80 ? 'text-red-600' : p?.score_riesgo >= 50 ? 'text-amber-600' : 'text-slate-700'}>{p?.score_riesgo || 0}</strong></span>
      </div>
    </div>
  );
}

function Metrics({ p }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="bg-white rounded border border-slate-200 p-2">
        <div className="flex items-center gap-1 text-[10px] uppercase text-slate-500"><AlertTriangle size={10} />Alertas</div>
        <div className="text-lg font-bold text-slate-800">{p?.alertas_count || 0}</div>
      </div>
      <div className="bg-white rounded border border-slate-200 p-2">
        <div className="flex items-center gap-1 text-[10px] uppercase text-slate-500"><DollarSign size={10} />Monto total</div>
        <div className="text-lg font-bold text-slate-800">{fmtCur(p?.monto_total)}</div>
      </div>
      <div className="bg-white rounded border border-slate-200 p-2">
        <div className="flex items-center gap-1 text-[10px] uppercase text-slate-500"><Hash size={10} />Movimientos</div>
        <div className="text-lg font-bold text-slate-800">{p?.movimientos_count || 0}</div>
      </div>
    </div>
  );
}

function AlertasList({ alertas, commonTypes }) {
  if (!alertas?.length) return <div className="text-[11px] text-slate-400 italic py-2">Sin alertas</div>;
  const commonSet = new Set(commonTypes || []);
  return (
    <ul className="space-y-1.5">
      {alertas.map((a, idx) => {
        const shared = commonSet.has(a.tipo);
        return (
          <li key={idx} className={`text-[11px] rounded border px-2 py-1.5 ${shared ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-slate-800 truncate flex items-center gap-1">
                  {shared && <span className="text-rose-600 text-[10px] font-bold">●</span>}
                  {a.tipo || 'Alerta'}
                </div>
                <div className="text-slate-500 truncate">{a.descripcion || a.cuenta_id || ''}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-semibold text-slate-800">{fmtCur(a.monto_total)}</div>
                {a.severidad && <div className="text-[9px] text-slate-500 uppercase">{a.severidad}</div>}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TopCuentas({ top, commonCuentas }) {
  if (!top?.length) return <div className="text-[11px] text-slate-400 italic py-2">Sin datos</div>;
  const commonSet = new Set((commonCuentas || []).map((c) => c.cuenta));
  return (
    <ul className="space-y-1">
      {top.map((c, idx) => (
        <li key={idx} className={`text-[11px] rounded px-2 py-1 flex items-center justify-between border ${commonSet.has(c.cuenta) ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
          <span className="font-mono text-slate-700 truncate">{c.cuenta}</span>
          <span className="text-slate-600 flex-shrink-0">
            {c.alertas} alerta{c.alertas !== 1 ? 's' : ''} · {fmtCur(c.monto)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Conclusion({ text }) {
  if (!text) return <div className="text-[11px] text-slate-400 italic py-2">Sin conclusión registrada</div>;
  return (
    <div className="text-[11px] text-slate-700 whitespace-pre-wrap bg-emerald-50 border border-emerald-200 rounded px-3 py-2 max-h-56 overflow-y-auto">
      {text}
    </div>
  );
}

export default function CasoComparativaTab({ caso, casosAnteriores, onIncorporated }) {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [incorporating, setIncorporating] = useState(false);

  const normalized = (casosAnteriores || [])
    .map((c) => (typeof c === 'string' ? { id: c } : c))
    .filter((c) => c && c.id);

  useEffect(() => {
    if (!selectedId && normalized.length > 0) {
      setSelectedId(normalized[0].id);
    }
  }, [normalized, selectedId]);

  useEffect(() => {
    let cancel = false;
    if (!caso?.id || !selectedId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/cases/${caso.id}/comparison/${selectedId}`);
        if (!cancel) setData(res.data);
      } catch {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [caso?.id, selectedId]);

  if (!normalized.length) {
    return (
      <div className="p-6 text-center text-sm text-slate-500">
        Este caso no tiene casos anteriores del mismo cliente para comparar.
      </div>
    );
  }

  const actual = data?.actual;
  const anterior = data?.anterior;
  const comunes = data?.comunes || {};
  const alreadyIncorporated = (caso?.contexto_incorporado || []).includes(selectedId);

  const handleIncorporate = async () => {
    if (!caso?.id || !selectedId) return;
    setIncorporating(true);
    try {
      await api.post(`/cases/${caso.id}/incorporate-previous`, { previous_case_id: selectedId });
      toast.success('Contexto incorporado al caso actual');
      if (onIncorporated) onIncorporated(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al incorporar');
    } finally {
      setIncorporating(false);
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="comparativa-tab">
      {/* Selector */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-slate-600">Comparar contra:</span>
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="w-80 bg-white" data-testid="comparativa-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {normalized.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.titulo || `Caso ${c.id.slice(0, 8)}`} — {c.estado || '—'} {c.fecha ? ` · ${fmtDate(c.fecha)}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {alreadyIncorporated ? (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
              <CheckCircle2 size={12} />
              Ya incorporado
            </span>
          ) : (
            <button
              onClick={handleIncorporate}
              disabled={incorporating}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white transition-colors"
              data-testid="comparativa-incorporate-btn"
            >
              {incorporating ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
              Incorporar a investigación actual
            </button>
          )}
          <button
            onClick={() => navigate(`/m/gestion-de-casos/caso/${selectedId}`)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-slate-700 hover:bg-slate-800 text-white transition-colors"
            data-testid="comparativa-open-btn"
          >
            <ExternalLink size={12} />
            Abrir anterior
          </button>
        </div>
      </div>

      {/* Badges de coincidencias */}
      {(comunes.cuentas_comunes?.length > 0 || comunes.tipos_comunes?.length > 0 || typeof comunes.monto_variacion_pct === 'number') && (
        <div className="flex flex-wrap gap-2">
          {comunes.cuentas_comunes?.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
              <Users size={11} />
              {comunes.cuentas_comunes.length} cuenta{comunes.cuentas_comunes.length !== 1 ? 's' : ''} en común
            </span>
          )}
          {comunes.tipos_comunes?.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-300">
              <AlertTriangle size={11} />
              {comunes.tipos_comunes.length} tipo{comunes.tipos_comunes.length !== 1 ? 's' : ''} de alerta repetido{comunes.tipos_comunes.length !== 1 ? 's' : ''}
            </span>
          )}
          {typeof comunes.monto_variacion_pct === 'number' && (
            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${comunes.monto_variacion_pct >= 0 ? 'bg-red-100 text-red-800 border-red-300' : 'bg-green-100 text-green-800 border-green-300'}`}>
              {comunes.monto_variacion_pct >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              Monto {comunes.monto_variacion_pct >= 0 ? '+' : ''}{comunes.monto_variacion_pct}%
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-10 gap-2 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Cargando comparativa...
        </div>
      )}

      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Actual */}
          <div className="space-y-3">
            <Header p={actual} variant="actual" />
            <Metrics p={actual} />
            <div className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1"><AlertTriangle size={10} />Alertas</div>
              <AlertasList alertas={actual?.alertas} commonTypes={(comunes.tipos_comunes || []).map((t) => t.tipo)} />
            </div>
            <div className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1"><Users size={10} />Top 5 cuentas</div>
              <TopCuentas top={actual?.top_cuentas} commonCuentas={comunes.cuentas_comunes} />
            </div>
            <div className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1"><FileText size={10} />Conclusión</div>
              <Conclusion text={actual?.conclusion} />
            </div>
          </div>

          {/* Anterior */}
          <div className="space-y-3">
            <Header p={anterior} variant="anterior" />
            <Metrics p={anterior} />
            <div className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1"><AlertTriangle size={10} />Alertas</div>
              <AlertasList alertas={anterior?.alertas} commonTypes={(comunes.tipos_comunes || []).map((t) => t.tipo)} />
            </div>
            <div className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1"><Users size={10} />Top 5 cuentas</div>
              <TopCuentas top={anterior?.top_cuentas} commonCuentas={comunes.cuentas_comunes} />
            </div>
            <div className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1"><FileText size={10} />Conclusión</div>
              <Conclusion text={anterior?.conclusion} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
